import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlanBadge } from "@/components/PlanBadge";
import { PLAN_META, requiredPlanFromMessage } from "@/lib/planFeatures";
import { describeCoupon } from "@/pages/ChoosePlan";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { startPlanCheckout } from "@/lib/billing";

export function UpgradeDialogHost() {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState("growth");
  const [message, setMessage] = useState("");
  const [annual, setAnnual] = useState(true);
  const [busy, setBusy] = useState(false);
  const [myCoupon, setMyCoupon] = useState(null);
  const openRef = useRef(false);
  const planRef = useRef(company?.plan);
  planRef.current = company?.plan;

  useEffect(() => {
    const handler = (e) => {
      if (openRef.current) return;
      const d = e.detail || {};
      setPlan(d.plan || requiredPlanFromMessage(d.message, planRef.current));
      setMessage(d.message || "");
      setOpen(true);
      openRef.current = true;
      api.get("/payments/my-coupon").then(({ data }) => data.has_coupon && setMyCoupon(data)).catch(() => {});
    };
    window.addEventListener("tenx:upgrade-required", handler);
    return () => window.removeEventListener("tenx:upgrade-required", handler);
  }, []);

  if (!user || user.role !== "founder") return null;

  const meta = PLAN_META[plan] || PLAN_META.growth;
  const price = annual ? Math.round(meta.yearly / 12) : meta.monthly;

  const close = () => { setOpen(false); openRef.current = false; setBusy(false); };

  const upgrade = async () => {
    setBusy(true);
    try {
      await startPlanCheckout(`${plan}_${annual ? "yearly" : "monthly"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <PlanBadge plan={plan} />
          </div>
          <DialogTitle className="text-xl" data-testid="upgrade-dialog-title">Upgrade to {meta.name}</DialogTitle>
          <DialogDescription data-testid="upgrade-dialog-message">
            {message || `${meta.tagline} Unlock it in seconds.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border bg-secondary/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="text-3xl font-bold text-foreground" data-testid="upgrade-price">${price}</span>
              <span className="text-sm text-muted-foreground">/mo{annual ? " · billed annually" : ""}</span>
            </div>
            <div className="inline-flex items-center rounded-full border bg-white p-0.5">
              <button
                onClick={() => setAnnual(false)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${!annual ? "bg-slate-900 text-white" : "text-slate-600"}`}
                data-testid="upgrade-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${annual ? "bg-slate-900 text-white" : "text-slate-600"}`}
                data-testid="upgrade-toggle-annual"
              >
                Annual <span className={annual ? "text-brand" : "text-brand-ink"}>-17%</span>
              </button>
            </div>
          </div>
          <ul className="mt-4 space-y-2">
            {meta.benefits.map((b, i) => (
              <li key={b} className="flex items-start gap-2 text-sm font-medium text-slate-700" data-testid={`upgrade-benefit-${i}`}>
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" strokeWidth={2.2} /> {b}
              </li>
            ))}
          </ul>
        </div>

        {myCoupon && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="upgrade-coupon-banner">
            <Gift className="h-3.5 w-3.5 shrink-0" /> {describeCoupon(myCoupon)} - applied automatically at checkout
          </div>
        )}

        <Button onClick={upgrade} disabled={busy} className="w-full gap-1.5" data-testid="upgrade-now-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Upgrade to {meta.name} now
        </Button>
        <div className="flex items-center justify-between">
          <button onClick={close} className="text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="upgrade-later-btn">
            Maybe later
          </button>
          <button
            onClick={() => { close(); navigate("/app/billing"); }}
            className="text-sm font-medium text-primary hover:underline"
            data-testid="upgrade-compare-plans"
          >
            Compare all plans
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
